import * as React from "react";
import { Component, FormEvent } from "react";
import { Route, RouteComponentProps } from "react-router-dom";
import Moment from "react-moment";
import StudyApi, { Study, StudyQuery } from "../../api/StudyApi";
import ObsBadge from "../../component/observation/ObsBadge";
import StudyDetail from "../study-detail/StudyDetail";
import EventBus, { Event, Events } from "../../service/EventBus";
import FileChooser from "../../component/filechooser/FileChooser";
import LabelApi from "../../api/LabelApi";
import MessageService from "../../service/MessageService";
import GlmId from "../../component/GlmId";
import { AuthManager } from "../../component/auth/AuthContext";
import SSEButton, { ActionEvent } from "../../component/SSEButton";
import StudyActionApi from "../../api/StudyActionApi";
import GlmQuery from "../../component/GlmQuery";
import { Input, InputGroup } from "reactstrap";
import { Query, QueryMode } from "../../api/Query";
import i18n from "i18next";
import { Page } from "../../component/Pagination";

import "./StudyList.scss";

interface StudyListProp extends RouteComponentProps<string> {
  authManager: AuthManager;
}

interface StudyListState {
  loading: boolean;
  currentIdx: number;
  page: Page;
  items: Array<Study>;
  query: StudyQuery;
  isQueryDirty: boolean;
}

export default class StudyList extends Component<StudyListProp, StudyListState> {
  private subIds: Array<string> = [];
  private studyApi: StudyApi;
  private studyActionApi: StudyActionApi;
  private labelApi: LabelApi;

  constructor(props: StudyListProp) {
    super(props);

    const qStr = window.localStorage.getItem("study-list-query");
    const query = qStr ? JSON.parse(qStr) : { q: "", from: "", to: "" };

    if (!query.mode) {
      query.mode = QueryMode.GLM;
    }

    this.studyApi = StudyApi.with(props.authManager.cancelableClient());
    this.studyActionApi = StudyActionApi.with(props.authManager.cancelableClient());
    this.labelApi = LabelApi.with(props.authManager.cancelableClient());

    this.state = {
      loading: false,
      currentIdx: -1,
      page: Page.default(),
      items: [],
      query: query,
      isQueryDirty: false,
    };
  }

  componentDidMount() {
    if (this.state.page.current < 0) this.moreResults();

    this.subIds.push(EventBus.subscribe(Events.study_finished, this.onStudyFinished.bind(this)));
    this.subIds.push(EventBus.subscribe(Events.study_next, this.onStudyNext.bind(this)));
  }

  componentWillUnmount() {
    for (const subId of this.subIds) {
      EventBus.unsubscribe(subId);
    }
    this.studyApi.cancel();
    this.labelApi.cancel();
  }

  onStudyNext() {
    // Next study
    if (this.state.currentIdx < this.state.items.length - 1) {
      this.setState((prevState) => {
        const cidx = prevState.currentIdx + 1;
        this.props.history.push("/study/" + this.state.items[cidx].id);

        return { currentIdx: cidx };
      });
    }
  }

  onStudyFinished(evt: Event) {
    const study = evt.target;

    // Reload current item
    if (this.state.currentIdx > -1 && study) {
      this.state.items[this.state.currentIdx] = study;
    }
  }

  moreResults() {
    this.setState({ loading: true }, () => {
      this.studyApi.searchStudies(this.state.page.nextPage(), this.state.query).then((searchResult) => {
        this.setState((prevState) => {
          const page = prevState.page;
          page.totalItems = searchResult.total;
          return {
            page: page,
            loading: false,
            items: prevState.items.concat(searchResult.resultItems),
          };
        });
      });
    });
  }

  search(evt?: MouseEvent | FormEvent) {
    window.localStorage.setItem("study-list-query", JSON.stringify(this.state.query));

    if (evt) evt.preventDefault();

    this.setState({ loading: true, items: [], page: this.state.page.reset(), isQueryDirty: false }, () => {
      this.studyApi.searchStudies(this.state.page.nextPage(), this.state.query).then((searchResult) => {
        this.setState((prevState) => {
          const page = prevState.page;
          page.totalItems = searchResult.total;
          return {
            page: page,
            loading: false,
            items: searchResult.resultItems,
          };
        });
      });
    });
  }

  processRule(): Promise<ActionEvent> {
    return this.studyActionApi.processStudyRule(this.state.query);
  }

  processInference(): Promise<ActionEvent> {
    const confirmInference = window.confirm(
      i18n.t("study.confirmInference", { count: this.state.page.totalItems || 0 })
    );

    if (confirmInference) {
      return this.studyActionApi.processStudyInference(this.state.query);
    }
  }

  onClickStudy(study: Study, idx: number) {
    this.props.history.push("/study/" + study.id);
    this.setState({ currentIdx: idx });
  }

  onFileToUpload(file: File) {
    this.labelApi.importLabel(file).then(
      (data: any) => MessageService.info(i18n.t("study.importSucceed") + data.count),
      (err: any) => MessageService.error(err)
    );
  }

  onQueryFromChanged(from: string) {
    this.setState((prevState) => {
      const query = prevState.query;
      query.from = from;
      return { query };
    });
  }

  onQueryToChanged(to: string) {
    this.setState((prevState) => {
      const query = prevState.query;
      query.to = to;
      return { query };
    });
  }

  onQueryChange(query: Query) {
    this.setState((prevState) => {
      const studyQuery = { ...prevState.query, ...query };
      return {
        query: studyQuery,
        isQueryDirty: true,
      };
    });
  }

  downloadLabelPath(query: any) {
    let params = "";
    Object.keys(query).forEach(function (p) {
      if (params) params += "&";
      else params = "?";

      params += p + "=" + encodeURI(query[p]);
    });

    return "/api/label/export" + params;
  }

  render() {
    return (
      <div>
        <Route path="/study/:id" render={(props) => <StudyDetail {...props} authManager={this.props.authManager} />} />

        <Route
          exact
          path="/study"
          render={() => {
            return (
              <div id="studies" className="study-list">
                <form
                  onSubmit={(evt) => this.search(evt)}
                  className="container-fluid m-2"
                  onKeyDownCapture={(evt) => this.onKeyDown(evt)}
                >
                  <div className="form-group row">
                    <div className="col-md-10">
                      <InputGroup>
                        {this.state.query.mode === QueryMode.GLM && (
                          <div>
                            <Input
                              type="text"
                              placeholder={i18n.t("studyList.from")}
                              name="from"
                              value={this.state.query.from}
                              onChange={(evt) => this.onQueryFromChanged(evt.target.value)}
                            />

                            <Input
                              type="text"
                              className="form-control"
                              placeholder={i18n.t("studyList.to")}
                              name="to"
                              value={this.state.query.to}
                              onChange={(evt) => this.onQueryToChanged(evt.target.value)}
                            />
                          </div>
                        )}

                        <GlmQuery onChange={(query) => this.onQueryChange(query)} query={this.state.query} />

                        <div className="input-group-append">
                          <button type="submit" className="btn btn-primary">
                            <i className="fa fa-search" />
                          </button>
                        </div>
                      </InputGroup>
                    </div>

                    <div className="col-md-2 study-list__action-buttons">
                      <a
                        href={this.downloadLabelPath(this.state.query)}
                        className="btn btn-secondary"
                        download
                        title="Download labels"
                      >
                        <i className="fa fa-download" />
                      </a>

                      <FileChooser
                        className="btn btn-secondary"
                        onFile={this.onFileToUpload.bind(this)}
                        accept=".json"
                        title="Upload labels"
                      >
                        <i className="fa fa-upload" />
                      </FileChooser>

                      <SSEButton
                        className="btn btn-secondary"
                        start={() => this.processRule()}
                        onFinish={() => this.search()}
                        disabled={this.state.isQueryDirty}
                      >
                        {i18n.t("study.processRules")}
                      </SSEButton>
                      <SSEButton
                        className="btn btn-secondary study-list__action-buttons__inference"
                        start={() => this.processInference()}
                        onFinish={() => this.search()}
                        disabled={this.state.isQueryDirty}
                      >
                        {i18n.t("study.processInference")}
                      </SSEButton>
                    </div>
                  </div>
                </form>

                <div className="pl-2">
                  {i18n.t("study.totalItems")} : {this.state.page.totalItems} / {i18n.t("study.itemPerPage")} :{" "}
                  {this.state.page.size} /{i18n.t("study.page")} : {this.state.page.current}
                </div>

                <table className="table table-striped table-dark">
                  <thead>
                    <tr>
                      <th>Id</th>
                      <th>{i18n.t("study.nbSeries")}</th>
                      <th>{i18n.t("study.indication")}</th>
                      <th>{i18n.t("study.observation")}</th>
                      <th>{i18n.t("global.patient")}</th>
                      <th>{i18n.t("study.date")}</th>
                      <th>{i18n.t("dicom.studyDescription")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {this.state.items.map((item, idx) => (
                      <tr key={item.id} onClick={(evt) => this.onClickStudy(item, idx)}>
                        <td>
                          <GlmId value={item.id} />
                        </td>
                        <td>{item.series.length}</td>
                        <td>{item.indication}</td>
                        <td>
                          {item.observations.map((obs) => (
                            <ObsBadge key={obs.id} obs={obs} />
                          ))}
                        </td>
                        <td>
                          {item.patientSex}, {item.patientAge} {i18n.t("global.yearOld")}
                        </td>
                        <td>
                          <Moment format="DD/MM/YYYY HH:mm">{item.studyDate}</Moment>
                        </td>
                        <td>{item.studyDescription}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="text-center">
                  {this.state.loading ? (
                    <div>
                      <i className="fa fa-spinner fa-spin" />
                    </div>
                  ) : (
                    <button className="btn" onClick={this.moreResults.bind(this)}>
                      {i18n.t("global.moreResults")}
                    </button>
                  )}
                </div>
              </div>
            );
          }}
        />
      </div>
    );
  }

  private onKeyDown(evt: React.KeyboardEvent<HTMLFormElement>) {
    // map CTRL + enter
    if ((evt.keyCode == 10 || evt.keyCode == 13) && evt.ctrlKey) {
      this.search(evt);
    }
  }
}
